import { useMemo } from "react";
import { useStore } from "@tanstack/react-form";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { orderComponentTree } from "../utils/component-tree";

/** Khóa của hợp phần trong form: id đã lưu, hoặc clientKey với hợp phần mới (API đổi sang id khi lưu) */
export const componentFormKey = (c: any): string => String(c?.id || c?.clientKey || "");

/**
 * Cấu trúc hợp phần của lệnh (form, tab 1): tên, hợp phần cha, dòng sản phẩm.
 * Định mức vật tư (tab 2), khuôn/kẽm (tab 3) và công đoạn (tab 4) khai báo theo từng hợp phần ở các tab đó.
 */
export function ComponentTreeEditor({ form }: { form: any }) {
  const components: any[] = useStore(form.store, (s: any) => s.values.components) || [];
  const sources: any[] = useStore(form.store, (s: any) => s.values.orderSources) || [];

  const parentKeyOf = (c: any) => c.parentKey ?? c.parentComponentId ?? null;
  const sourceDetailOf = (c: any) =>
    c.orderDetailId ?? sources.find((s: any) => s.id && s.id === c.productionOrderSourceId)?.orderDetailId ?? "";

  // Thứ tự hiển thị theo cây (cha trước con); dùng khóa form để gồm cả hợp phần chưa lưu
  const ordered = useMemo(() => {
    const keyed = components.map((c, i) => ({ ...c, id: componentFormKey(c) || `idx-${i}`, parentComponentId: parentKeyOf(c), __index: i }));
    return orderComponentTree(keyed);
  }, [components]);

  const descendantsOf = (key: string): Set<string> => {
    const out = new Set<string>();
    const stack = [key];
    while (stack.length) {
      const k = stack.pop()!;
      for (const c of components) {
        const ck = componentFormKey(c);
        if (ck && parentKeyOf(c) === k && !out.has(ck)) {
          out.add(ck);
          stack.push(ck);
        }
      }
    }
    return out;
  };

  const update = (index: number, patch: Record<string, unknown>) => {
    const next = [...components];
    next[index] = { ...next[index], ...patch };
    form.setFieldValue("components", next);
  };

  const add = () => {
    const next = [
      ...components,
      {
        clientKey: `new-comp-${Date.now().toString(36)}`,
        componentName: "",
        isMain: components.length === 0,
        routingSteps: [],
        bomItems: [],
        parentKey: null,
        orderDetailId: sources.length === 1 ? sources[0].orderDetailId || null : null,
        productionOrderSourceId: null,
      },
    ];
    form.setFieldValue("components", next);
    if (next.length > 1) form.setFieldValue("isComposite", true);
  };

  const remove = (index: number) => {
    const key = componentFormKey(components[index]);
    const next = components
      .filter((_, i) => i !== index)
      // Hợp phần con của hợp phần bị xóa lên làm gốc
      .map((c) => (parentKeyOf(c) === key ? { ...c, parentKey: null, parentComponentId: null } : c));
    form.setFieldValue("components", next);
  };

  return (
    <div className="space-y-2" data-testid="po-form-component-tree">
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full min-w-[720px] text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-1.5 text-left w-10">#</th>
              <th className="px-2 py-1.5 text-left">Hợp phần</th>
              <th className="px-2 py-1.5 text-left w-56">Thuộc hợp phần</th>
              <th className="px-2 py-1.5 text-left w-64">Dòng sản phẩm của lệnh</th>
              <th className="px-2 py-1.5 text-right w-28">Công đoạn · Vật tư</th>
              <th className="px-2 py-1.5 w-10" />
            </tr>
          </thead>
          <tbody>
            {ordered.map((node, row) => {
              const index = node.component.__index as number;
              const c = components[index];
              const key = componentFormKey(c);
              const blocked = key ? descendantsOf(key) : new Set<string>();
              const nSteps = (c.routingSteps || []).length;
              const nBom = (c.bomItems || []).length;
              const locked = nSteps + nBom > 0;
              return (
                <tr key={key || index} data-testid="po-form-component-row" className="border-t border-slate-100">
                  <td className="px-2 py-1 text-slate-400 font-mono">{row + 1}</td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1.5" style={{ paddingLeft: node.depth * 16 }}>
                      {node.depth > 0 && <span className="text-indigo-300 font-mono">└</span>}
                      <Input
                        aria-label="Tên hợp phần"
                        value={c.componentName || ""}
                        onChange={(e) => update(index, { componentName: e.target.value })}
                        placeholder="Vd: Thân hộp, Nắp, Lót"
                        className="h-8 text-xs"
                      />
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <select
                      aria-label="Thuộc hợp phần"
                      className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs"
                      value={parentKeyOf(c) || ""}
                      onChange={(e) => {
                        const v = e.target.value || null;
                        update(index, {
                          parentKey: v,
                          parentComponentId: v && !v.startsWith("new-comp-") ? v : null,
                        });
                      }}
                    >
                      <option value="">— Cấp gốc —</option>
                      {components.map((p, pi) => {
                        const pk = componentFormKey(p);
                        if (!pk || pk === key || blocked.has(pk)) return null;
                        return (
                          <option key={pk} value={pk}>
                            {p.componentName || `Hợp phần ${pi + 1}`}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    {parentKeyOf(c) ? (
                      <span className="text-slate-400">Theo hợp phần cha</span>
                    ) : (
                      <select
                        aria-label="Dòng sản phẩm của lệnh"
                        className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs"
                        value={sourceDetailOf(c) || ""}
                        onChange={(e) => update(index, { orderDetailId: e.target.value || null, productionOrderSourceId: null })}
                      >
                        <option value="">— Dùng chung cho cả lệnh —</option>
                        {sources
                          .filter((s: any) => s.orderDetailId)
                          .map((s: any, si: number) => (
                            <option key={s.orderDetailId} value={s.orderDetailId}>
                              {s.productName || s.orderDetail?.productName || `Dòng ${si + 1}`}
                            </option>
                          ))}
                      </select>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right text-slate-500 tabular-nums">
                    {nSteps} · {nBom}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                      onClick={() => remove(index)}
                      disabled={locked || components.length <= 1}
                      title={locked ? "Hợp phần đã có công đoạn hoặc định mức vật tư, xóa các dòng đó trước" : "Xóa hợp phần"}
                      aria-label="Xóa hợp phần"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={add}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Thêm hợp phần
      </Button>
    </div>
  );
}
